import { useNavigate } from 'react-router-dom'
import { X, MoreHorizontal } from 'lucide-react'
import { useState, useRef, useEffect, useCallback } from 'react'
import VideoPlayer from '../components/VideoPlayer'
import IconButton from '../components/ui/IconButton'
import PostActions from '../components/PostActions'
import PostOptionsDropdown from '../components/PostOptionsDropdown'
import { useMediaPreview } from '../contexts/MediaPreviewContext'
import { useSocialInteractions } from '../hooks/useSocialInteractions'

export default function MediaPreviewOverlay() {
  const navigate = useNavigate()
  const { previewData: state, closePreview } = useMediaPreview()

  const [showOptionsMenu, setShowOptionsMenu] = useState(false)
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | undefined>(undefined)
  const moreButtonRef = useRef<HTMLButtonElement>(null)
  const mediaRef = useRef<HTMLDivElement>(null)
  const [mediaWidth, setMediaWidth] = useState(0)

  const [visible, setVisible] = useState(false)
  const [animatingOut, setAnimatingOut] = useState(false)

  const socialInteractions = useSocialInteractions(state?.post?.id || '')
  const {
    liked: isLiked,
    saved: isBookmarked,
    reposted: isReposted,
    likesCount,
    commentsCount,
    repostsCount,
    loading,
    actions: { toggleLike, toggleSave, toggleRepost, sharePost }
  } = socialInteractions

  // Track media element width
  useEffect(() => {
    if (!mediaRef.current) return
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setMediaWidth(entry.contentRect.width)
      }
    })
    observer.observe(mediaRef.current)
    return () => observer.disconnect()
  }, [state])

  useEffect(() => {
    if (state) {
      setShowOptionsMenu(false)
      setDragY(0)
      setIsDragging(false)
      requestAnimationFrame(() => setVisible(true))
    }
  }, [state])

  const handleClose = useCallback(() => {
    setAnimatingOut(true)
    setVisible(false)
    setTimeout(() => {
      closePreview()
      setAnimatingOut(false)
    }, 200)
  }, [closePreview])

  useEffect(() => {
    if (!state) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [state, handleClose])

  const touchStartY = useRef<number | null>(null)
  const [dragY, setDragY] = useState(0)
  const [isDragging, setIsDragging] = useState(false)

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartY.current === null) return
    const delta = e.touches[0].clientY - touchStartY.current
    if (delta > 0) {
      setDragY(delta)
      setIsDragging(true)
    }
  }

  const handleTouchEnd = () => {
    if (dragY > 150) {
      handleClose()
    } else {
      setDragY(0)
      setIsDragging(false)
    }
    touchStartY.current = null
  }

  if (!state) return null

  const handleMoreClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (moreButtonRef.current) {
      const rect = moreButtonRef.current.getBoundingClientRect()
      setMenuPosition({ x: rect.left, y: rect.bottom })
    }
    setShowOptionsMenu(true)
  }

  const isOwner = state.currentUserId === state.post?.authorId

  const handleLike = (e?: React.MouseEvent) => { e?.stopPropagation(); toggleLike() }
  const handleComment = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    handleClose()
    if (state.post?.id) navigate(`/post/${state.post.id}`)
  }
  const handleRepost = (e?: React.MouseEvent) => { e?.stopPropagation(); toggleRepost() }
  const handleShare = (e?: React.MouseEvent) => { e?.stopPropagation(); sharePost() }
  const handleBookmark = (e?: React.MouseEvent) => { e?.stopPropagation(); toggleSave() }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={handleClose}
      style={{
        opacity: visible && !animatingOut ? 1 : 0,
        transition: 'opacity 0.2s ease',
      }}
    >
      <div className="absolute inset-0 bg-black/95" />

      <div className="absolute top-4 left-4 z-10">
        <IconButton
          onClick={handleClose}
          ariaLabel="Close preview"
          className="text-white hover:bg-white/20"
        >
          <X className="w-6 h-6" />
        </IconButton>
      </div>

      {state.post && (
        <div className="absolute top-4 right-4 z-10">
          <button
            ref={moreButtonRef}
            onClick={handleMoreClick}
            className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
          >
            <MoreHorizontal className="w-6 h-6" />
          </button>
        </div>
      )}

      {/* Media + Actions column */}
      <div
        className="relative flex flex-col items-center justify-center pointer-events-none"
        style={{
          width: '100%',
          height: '100%',
          transform: isDragging
            ? `translateY(${dragY}px)`
            : visible && !animatingOut
              ? 'scale(1)'
              : 'scale(0.95)',
          opacity: isDragging ? Math.max(0.2, 1 - dragY / 400) : 1,
          transition: isDragging ? 'none' : 'transform 0.2s ease, opacity 0.2s ease',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Media */}
        <div
          ref={mediaRef}
          className="pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {state.mediaType === 'video' ? (
            <VideoPlayer
              src={state.mediaUrl}
              autoPlay
              controls
              className="max-w-[90vw] max-h-[80vh] object-contain relative z-10"
            />
          ) : (
            <img
              src={state.mediaUrl}
              alt={state.post?.content || 'Media preview'}
              className="max-w-[90vw] max-h-[80vh] object-contain"
            />
          )}
        </div>

        {/* PostActions — pinned below media, matching its width */}
        {state.post && (
          <div
            className="pointer-events-auto mt-2"
            style={{ width: mediaWidth || 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <PostActions
              stats={{
                likes: likesCount || state.post.stats?.likes || 0,
                comments: commentsCount || state.post.stats?.comments || 0,
                reposts: repostsCount || state.post.stats?.reposts || 0,
                views: state.post.stats?.views || 0,
              }}
              onLike={handleLike}
              onComment={handleComment}
              onRepost={handleRepost}
              onShare={handleShare}
              onBookmark={handleBookmark}
              isLiked={isLiked}
              isBookmarked={isBookmarked}
              isReposted={isReposted}
              loading={loading}
              hasMedia={!!state.mediaUrl}
              onRecordCode={state.post.onRecordCode}
              location={state.post.location}
              capturedAt={state.post.capturedAt}
              createdAt={state.post.createdAt}
              className="border-t-white/10 text-white/80"
            />
          </div>
        )}

        {/* Gallery context */}
        {!state.post && state.fromGallery && (
          <div className="pointer-events-auto mt-4 flex justify-center" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleClose()
                navigate('/create', { state: { mediaUrl: state.mediaUrl, mediaType: state.mediaType } })
              }}
              className="px-8 py-3 bg-onrecord-orange text-white rounded-full text-sm font-bold hover:bg-onrecord-orange-dark transition-colors shadow-lg"
            >
              Create Post
            </button>
          </div>
        )}
      </div>

      {state.post && (
        <PostOptionsDropdown
          isOpen={showOptionsMenu}
          onClose={() => setShowOptionsMenu(false)}
          isOwner={isOwner}
          post={{
            id: state.post.id || 'unknown',
            authorId: state.post.authorId,
            content: state.post.content,
            author: state.post.author,
          }}
          buttonPosition={menuPosition}
        />
      )}
    </div>
  )
}
